// src/StudentTaxonomyEditor.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { getTaxonomy } from './api';
import TaxonomyMultiSelect from './TaxonomyMultiSelect';

export default function StudentTaxonomyEditor({
  value = { needs: [] }, // needs: [{code, priority, required}] | ["CODE"]
  onChange
}) {
  const [tx, setTx] = useState({});
  const [loaded, setLoaded] = useState(false);

  // --- load taxonomy
  useEffect(() => {
    getTaxonomy()
      .then(res => setTx(res.data || {}))
      .finally(() => setLoaded(true));
  }, []);

  // helperi
  const toCode = (x) => (typeof x === 'string' ? x : x?.code);
  const normNeed = (x) =>
    (typeof x === 'string'
      ? { code: x, priority: 1, required: false }
      : { priority: 1, required: false, ...x });

  const isProgramItem = (item) => {
    const code = String(item?.code || '').toUpperCase();
    const cat = String(item?.category || '').toLowerCase();
    return code.startsWith('PROG_') || cat === 'program';
  };

  // --- sortiranje: domain prvo, zatim ostale kategorije po abecedi; unutar kategorije po label/code
  const catRank = (cat) => (String(cat).toLowerCase() === 'domain' ? 0 : 1);

  // sve stavke (bez PROG_*/program)
  const allItems = useMemo(() => {
    const arr = [];
    for (const [, list] of Object.entries(tx || {})) {
      for (const it of list) {
        if (!isProgramItem(it)) arr.push(it);
      }
    }
    return arr.sort((a, b) => {
      const ar = catRank(a.category);
      const br = catRank(b.category);
      if (ar !== br) return ar - br; // domain grupa prva
      const ac = (a.category || '').localeCompare(b.category || '');
      if (ac !== 0) return ac;       // ostale kategorije po abecedi
      return (a.label || a.code).localeCompare(b.label || b.code); // unutar kategorije po label/code
    });
  }, [tx]);

  // mapa code -> item (radi prikaza labela)
  const codeToItem = useMemo(() => {
    const m = new Map();
    for (const it of allItems) m.set(it.code, it);
    return m;
  }, [allItems]);

  // selekcija (Set kodova)
  const selectedCodes = useMemo(() => {
    const s = new Set();
    for (const n of (value.needs || [])) {
      const c = toCode(n);
      if (c) s.add(c);
    }
    return s;
  }, [value.needs]);

  // jedokratno očisti PROG_* ako je eventualno već upisano u needs (posle load-a)
  useEffect(() => {
    if (!loaded) return;
    const cleaned = (value.needs || [])
      .map(normNeed)
      .filter(n => {
        const item = codeToItem.get(n.code);
        if (!item) {
          const code = String(n.code || '').toUpperCase();
          return !code.startsWith('PROG_');
        }
        return true;
      });
    if (cleaned.length !== (value.needs || []).length) {
      onChange && onChange({ ...value, needs: cleaned });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, codeToItem]);

  // toggle Need
  const toggleNeed = (code) => {
    const exists = selectedCodes.has(code);
    let needs = (value.needs || []).map(normNeed);

    if (exists) {
      needs = needs.filter(n => n.code !== code);
    } else {
      needs = [...needs, { code, priority: 1, required: false }];
    }
    onChange && onChange({ ...value, needs });
  };

  // meta (priority/required)
  const setNeedMeta = (code, patch) => {
    const needs = (value.needs || [])
      .map(normNeed)
      .map(n => (n.code === code ? { ...n, ...patch } : n));
    onChange && onChange({ ...value, needs });
  };

  // prikaz detalja – po abecedi (label/code)
  const selectedList = useMemo(() => {
    const list = [...selectedCodes].map(code => ({
      code,
      item: codeToItem.get(code)
    }));
    return list.sort((a, b) => {
      const la = (a.item?.label || a.code);
      const lb = (b.item?.label || b.code);
      return la.localeCompare(lb);
    });
  }, [selectedCodes, codeToItem]);

  return (
    <div>
      <TaxonomyMultiSelect
        title="Student Needs & Preferences"
        items={allItems}                 // bez PROG_*
        selectedCodes={selectedCodes}
        onToggle={toggleNeed}
        groupByCategory={true}
      />

      {selectedList.length > 0 && (
        <div className="mt-2 border rounded p-2">
          <div className="text-sm font-semibold mb-1">Need details</div>
          <div className="space-y-2">
            {selectedList.map(({ code, item }) => {
              const n = (value.needs || []).map(normNeed).find(x => x.code === code) || { code, priority: 1, required: false };
              const label = item?.label || code;
              const category = item?.category ? ` · ${item.category}` : '';
              return (
                <div key={code} className="flex flex-wrap items-center gap-2">
                  <span className="text-sm w-60 truncate" title={`${label}${category}`}>{label}<span className="text-xs text-slate-500">{category}</span></span>

                  <label className="text-sm flex items-center gap-1">
                    Priority:
                    <select
                      value={n.priority || 1}
                      onChange={e => setNeedMeta(code, { priority: Number(e.target.value) })}
                      className="border rounded px-2 py-1 text-sm"
                    >
                      <option value={1}>1 (low)</option>
                      <option value={2}>2</option>
                      <option value={3}>3 (high)</option>
                    </select>
                  </label>

                  <label className="text-sm flex items-center gap-1">
                    Required
                    <input
                      type="checkbox"
                      checked={!!n.required}
                      onChange={e => setNeedMeta(code, { required: e.target.checked })}
                    />
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}



